import { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";

import { updateInventories } from "@/redux/actions/inventory.actions";
import { formatMiles, formatMilesDecimalNew } from "@/services/Utils";

import CustomTooltip from "@/components/desktop/tooltip/CustomTooltip";

import { ReactComponent as ArrowUpSvg } from "@/assets/images/desktop/Flechaverdehorizontal.svg";
import { ReactComponent as ArrowDownSvg } from "@/assets/images/desktop/flechaverdeabajo.svg";
import { ReactComponent as AlertSvg } from "@/assets/images/desktop/alert.svg";

import classes from "./inventory.module.scss";
import InventoryTableItem from "./InventoryTableItem";

import apiService from "@/services/api.service";
import { CHANGE_SELECT_INVENTORIES } from "@/redux/types/inventory.types";
import EditMultiple from "@/components/Table/EditMultiple/EditMultiple";

const InventoryTableProduct = ({ index, handleShowMovements }) => {
	const { addToast } = useToasts();
	const dispatch = useDispatch();

	const { inventories, aptitudes, active_ingredients, selected } = useSelector((state) => state.inventory);

	const [localAptitudes, setLocalAptitudes] = useState([]);
	const [localActiveIngredients, setLocalActiveIngredients] = useState([]);

	useEffect(() => {
		if (localAptitudes.length === 0) {
			setLocalAptitudes(aptitudes);
		}
		if (localActiveIngredients.length === 0) {
			setLocalActiveIngredients(active_ingredients);
		}
		// eslint-disable-next-line
	}, [aptitudes, active_ingredients]);

	const handleClick = () => {
		let newInventories = [...inventories];
		newInventories[index].active = !inventories[index].active;

		dispatch(updateInventories(newInventories));
	};

	const handleClickAction = () => {
		let newInventories = [...inventories];
		newInventories[index].show_action = !inventories[index].show_action;
		dispatch(updateInventories(newInventories));
	};

	const handleEditAptitude = async (value) => {
		const form = {
			product_id: inventories[index].PRODUCT_ID,
			aptitude: value,
		};

		await apiService.post("/admin/products/update/aptitudes/custom", { ...form }).then((response) => {
			if (Object.prototype.hasOwnProperty.call(response, "text_aptitudes")) {
				const newInventories = [...inventories];
				newInventories[index].APTITUD = response.text_aptitudes;
				dispatch(updateInventories(newInventories));

				addToast("Se ha guardado correctamente la aptitud del producto.", {
					appearance: "success",
				});
			}
		});
	};

	const handleEditActiveIngredient = async (value) => {
		const form = {
			product_id: inventories[index].PRODUCT_ID,
			active_ingredient: value,
		};

		await apiService.post("/admin/products/update/active_ingredients/custom", { ...form }).then((response) => {
			if (Object.prototype.hasOwnProperty.call(response, "text_active_ingredients")) {
				const newInventories = [...inventories];
				newInventories[index].INGREDIENTE_ACTIVO = response.text_active_ingredients;
				dispatch(updateInventories(newInventories));

				addToast("Se ha guardado correctamente el ingrediente activo del producto.", {
					appearance: "success",
				});
			}
		});
	};

	const checkProduct = (product) => {
		const inventories_details =
			product.INVENTORY_ID && Object.prototype.hasOwnProperty.call(product, "INVENTORY_ID")
				? product.INVENTORY_ID.split(",").map((id) => ({
						original_id: id,
						is_generic: product.CODIGO_PRODUCTO.startsWith("G") ? 1 : 0,
					}))
				: [];

		const isAllInSelected = inventories_details.every((detail) => selected.some((sel) => Number(sel.original_id) === Number(detail.original_id)));

		if (!isAllInSelected) {
			const newSelected = [
				...selected,
				...inventories_details.filter((detail) => !selected.some((sel) => Number(sel.original_id) === Number(detail.original_id))),
			];
			dispatch({
				type: CHANGE_SELECT_INVENTORIES,
				payload: {
					selected: newSelected,
				},
			});
		} else {
			const newSelected = selected.filter((s) => !inventories_details.some((detail) => Number(detail.original_id) === Number(s.original_id)));

			dispatch({
				type: CHANGE_SELECT_INVENTORIES,
				payload: {
					selected: newSelected,
				},
			});
		}
	};

	const isAllSelected = (inventoryIds) => {
		if (!inventoryIds) return false;

		const inventoriesDetails = inventoryIds.split(",").map((id) => Number(id));
		return inventoriesDetails.every((id) => selected.some((sel) => Number(sel.original_id) === Number(id)));
	};

	const otherActive = inventories.filter((fi) => fi.active && fi.product_id !== inventories[index].product_id);

	return (
		<tr key={index} className={`${classes.table_tr}  ${otherActive.length > 0 && classes.otherActive}`}>
			<td className={`${classes.table_td} ${classes.first_td}`}>
				<div className={`${classes.table_div} ${inventories[index].active && classes.active} ${classes.first_div}`}>
					<Form.Check
						key={`product_${inventories[index].INVENTORY_ID}`}
						type="checkbox"
						checked={isAllSelected(inventories[index].INVENTORY_ID)}
						onChange={(_e) => checkProduct(inventories[index])}
						onClick={() => checkProduct(inventories[index])}
					/>
				</div>
			</td>
			<td className={classes.table_td}>
				<div className={`${classes.table_div} ${inventories[index].active && classes.active}`}>
					{Number(String(inventories[index].STOCK).replace(",", ".")) < Number(String(inventories[index].STOCK_CRITICO).replace(",", ".")) && (
						<CustomTooltip
							description={`El stock actual es de ${formatMilesDecimalNew(inventories[index].STOCK)}${
								inventories[index].UNIDAD_DE_MEDIDA
							}, lo cual está por debajo del stock crítico establecido de ${formatMilesDecimalNew(inventories[index].STOCK_CRITICO)}${
								inventories[index].UNIDAD_DE_MEDIDA
							} `}
							text={<AlertSvg style={{ marginRight: "5px" }} />}
						/>
					)}

					{inventories[index].NOMBRE_COMERCIAL ? inventories[index].NOMBRE_COMERCIAL : ""}
				</div>
			</td>
			<td className={classes.table_td}>
				<div className={`${classes.table_div} ${inventories[index].active && classes.active} ${classes.editable} ${classes.center}`}>
					<EditMultiple
						options={localActiveIngredients}
						setOptions={setLocalActiveIngredients}
						handleChange={handleEditActiveIngredient}
						currentValue={inventories[index].INGREDIENTE_ACTIVO_ID}
						currentLabel={inventories[index].INGREDIENTE_ACTIVO}
						needPermission={true}
					/>
				</div>
			</td>
			<td className={classes.table_td}>
				<div className={`${classes.table_div} ${inventories[index].active && classes.active} ${classes.editable} ${classes.center}`}>
					<EditMultiple
						options={localAptitudes}
						setOptions={setLocalAptitudes}
						handleChange={handleEditAptitude}
						currentValue={inventories[index].APTITUD_ID}
						currentLabel={inventories[index].APTITUD}
						needPermission={true}
					/>
				</div>
			</td>
			<td className={classes.table_td}>
				<div className={`${classes.table_div} ${classes.number_value} ${inventories[index].active && classes.active}`}>
					{formatMiles(inventories[index].CANTIDAD_ENVASES)}
				</div>
			</td>
			<td className={classes.table_td}>
				<div className={`${classes.table_div} ${inventories[index].active && classes.active} ${classes.number_value}`}>
					<div className={`${classes.stock_number}`} onClick={() => handleShowMovements(inventories[index])}>
						<strong>{inventories[index].STOCK < 0 ? 0 : inventories[index].STOCK ? formatMilesDecimalNew(inventories[index].STOCK) : 0}</strong>
					</div>
				</div>
			</td>
			<td className={classes.table_td}>
				<div className={`${classes.table_div}  ${inventories[index].active && classes.active} ${classes.center}`}>
					{inventories[index].UNIDAD_DE_MEDIDA}
				</div>
			</td>
			<td className={classes.table_td} onClick={() => handleClickAction(index)}>
				<div style={{ position: "relative" }} className={`${classes.table_div} ${inventories[index].active && classes.active} ${classes.center}`}>
					<InventoryTableItem indice={index} handleShowMovements={handleShowMovements} />
				</div>
			</td>
			<td className={`${classes.table_td} ${classes.last_td}`} onClick={() => handleClick(index)}>
				<div className={`${classes.table_div} ${inventories[index].active && classes.active} ${classes.last_div}`}>
					{inventories[index].active ? <ArrowDownSvg /> : <ArrowUpSvg />}
				</div>
			</td>
		</tr>
	);
};

export default InventoryTableProduct;
